import { z } from 'zod';
import { parentIsRequired } from '../utils/dealerParentageRules';

// Validation messages start with the field's human name so they read naturally
// under each input (e.g. "Name is required", "Email must be a valid email address").
export const dealerSchema = z.object({
    dealer_code: z.string().min(1, 'Dealer Code is required').max(50, 'Dealer Code must be 50 characters or fewer').optional(),
    name: z.string().min(1, 'Name is required').max(255, 'Name must be 255 characters or fewer'),
    trading_name: z.string().max(255, 'Trading Name must be 255 characters or fewer').optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    email: z.string().email('Email must be a valid email address').max(255, 'Email must be 255 characters or fewer').optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    phone: z.string().max(30, 'Phone must be 30 characters or fewer').optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    website: z.string().max(255, 'Website must be 255 characters or fewer').optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    franchise_code: z.string().max(100, 'Franchise Code must be 100 characters or fewer').optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    status: z.string().min(1, 'Status is required').default('active'),
    type: z.enum(['dealer', 'dealer_group', 'oem', 'oem_group', 'franchise_dealer']).default('dealer'),
    parent_dealer_id: z.string().uuid('Owning Parent must be a valid dealer').optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    address_line_1: z.string().min(1, 'Address Line 1 is required').max(255, 'Address Line 1 must be 255 characters or fewer'),
    address_line_2: z.string().max(255, 'Address Line 2 must be 255 characters or fewer').optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    city: z.string().min(1, 'City is required').max(100, 'City must be 100 characters or fewer'),
    postcode: z.string().min(1, 'Postcode is required').max(20, 'Postcode must be 20 characters or fewer'),
    latitude: z.number({ invalid_type_error: 'Latitude must be a number' }).min(-90, 'Latitude must be between -90 and 90').max(90, 'Latitude must be between -90 and 90').optional(),
    longitude: z.number({ invalid_type_error: 'Longitude must be a number' }).min(-180, 'Longitude must be between -180 and 180').max(180, 'Longitude must be between -180 and 180').optional(),
    create_security_group: z.boolean().optional(),
}).superRefine((data, ctx) => {
    if (parentIsRequired(data.type) && !data.parent_dealer_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parent_dealer_id'],
            message: 'Owning Parent is required for this dealer type.',
        });
    }
});

export const dealerBankAccountSchema = z.object({
    account_name: z.string().min(1).max(255),
    account_number: z.string().regex(/^\d{8}$/),
    sort_code: z.string().regex(/^\d{6}$/),
    bank_name: z.string().max(255).optional().or(z.literal('')),
    is_primary: z.boolean().default(false),
});

export const dealerContactSchema = z.object({
    name: z.string().min(1).max(255),
    role: z.string().max(100).optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    email: z.string().email().optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    phone: z.string().max(30).optional().or(z.literal('')).transform(v => v === '' ? undefined : v),
    notify_agreements: z.boolean().default(false),
    notify_payments: z.boolean().default(false),
});

export const dealerMarginSchema = z.object({
    product_type: z.string().min(1).max(100),
    margin_bps: z.number().int().min(0).max(100000),
    effective_from: z.string().optional(),
    effective_to: z.string().optional(),
});

export type DealerFormInput = z.infer<typeof dealerSchema>;
export type DealerBankAccountFormInput = z.infer<typeof dealerBankAccountSchema>;
export type DealerContactFormInput = z.infer<typeof dealerContactSchema>;
export type DealerMarginFormInput = z.infer<typeof dealerMarginSchema>;
